/**
 * ISO-3166-1 alpha-2 country options for address forms.
 *
 * The code list is NOT hand-authored here — it comes from
 * `react-phone-number-input`'s `getCountries()` (already a dependency of this
 * package, used by `phone-input.tsx`), and the human labels come from the
 * platform's own `Intl.DisplayNames`. Nothing in this file invents a country
 * or a name, so the list cannot drift into fiction the way a pasted array can.
 */
import { getCountries } from "react-phone-number-input";

export interface CountryOption {
  /** ISO-3166-1 alpha-2, e.g. "CA" — the value Stripe expects for address.country */
  code: string;
  /** localized display name, e.g. "Canada" */
  label: string;
}

const displayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

export const COUNTRY_OPTIONS: CountryOption[] = getCountries()
  .map((code) => ({
    code: code as string,
    // fall back to the code itself rather than printing nothing or guessing
    label: displayNames?.of(code) ?? (code as string),
  }))
  .sort((a, b) => a.label.localeCompare(b.label, "en"));

export const isKnownCountryCode = (code: string): boolean =>
  COUNTRY_OPTIONS.some((option) => option.code === code);
